import { IntlShape } from 'react-intl';
import { z } from 'zod';
import {
    EMAIL_REGEX,
    FIRST_NAME_REGEX,
    LAST_NAME_REGEX,
} from '../../../utils/validators/regex';

export interface IFormInput {
    firstName: string;
    lastName: string;
    dob: string;
    zipcode: string;
    phoneNumber: string;
    emailAddress?: string;
    ssnRequired: string;
    ssnNotReqdReason?: string;
    callPermission: string;
    uniqueCallId: string;
    recordId: string;
    csrReferralFlow?: string;
}

export const DefaultValues: IFormInput = {
    firstName: '',
    lastName: '',
    dob: '',
    zipcode: '',
    phoneNumber: '',
    emailAddress: '',
    ssnRequired: 'YES',
    ssnNotReqdReason: '',
    callPermission: 'YES',
    uniqueCallId: '',
    recordId: '',
    csrReferralFlow: '',
};

// Define the Zod schema
export const userRegistrationSchema = (intl: IntlShape) =>
    z
        .object({
            firstName: z.string().trim().regex(
                FIRST_NAME_REGEX,
                intl.formatMessage({
                    id: 'member.first_name.validation.message',
                }),
            ),
            lastName: z.string().trim().regex(
                LAST_NAME_REGEX,
                intl.formatMessage({
                    id: 'member.last_name.validation.message',
                }),
            ),
            dob: z.union([
                z.date().max(
                    new Date(),
                    intl.formatMessage({
                        id: 'member.dob.validation.message',
                    }),
                ),
                z.string().min(
                    10,
                    intl.formatMessage({
                        id: 'member.dob.validation.message',
                    }),
                ),
            ]),
            zipcode: z.string().trim().refine(
                (value) => {
                    return value.toString().length === 5;
                },
                {
                    message: intl.formatMessage({
                        id: 'member.zip.validation.message',
                    }),
                },
            ),
            phoneNumber: z.string().trim().refine(
                (value) => {
                    return value.toString().length === 10;
                },
                {
                    message: intl.formatMessage({
                        id: 'member.phone_number.validation.message',
                    }),
                },
            ),

            emailAddress: z
                .string()
                .trim()
                .optional()
                .or(z.literal(''))
                .refine(
                    (val) => {
                        if (val) {
                            return EMAIL_REGEX.test(val);
                        }
                        return true;
                    },
                    {
                        message: intl.formatMessage({
                            id: 'member.email.validation.message',
                        }),
                    },
                ),
            ssnRequired: z
                .enum(['YES', 'NO'])
                .refine((val) => val === 'YES' || val === 'NO', {
                    message: intl.formatMessage({
                        id: 'member.ssn.validation.message',
                    }),
                }),
            ssnNotReqdReason: z.string().optional(),
            callPermission: z
                .enum(['YES', 'NO'])
                .refine((val) => val === 'YES' || val === 'NO', {
                    message: intl.formatMessage({
                        id: 'member.required.validation.message',
                    }),
                }),
            uniqueCallId: z.string().optional(),
            recordId: z.string().optional(),
        })
        .superRefine((data, ctx) => {
            // Check if ssn is 'YES' and ssnNotReqdReason is not provided
            if (data.ssnRequired === 'NO' && !data.ssnNotReqdReason) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['ssnNotReqdReason'],
                    message: intl.formatMessage({
                        id: 'member.ssn_reason.validation.message',
                    }),
                });
            }
        });
